"use client";

import Image from "next/image";
import { useState } from "react";

import MacDownloadModal from "@/components/interview/MacDownloadModal";
import { PUBLIC_PRODUCT_LINKS, PUBLIC_STORE_DEFAULTS } from "@/config/publicProductLinks";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDeviceSetupCopy, getDeviceSetupContent } from "@/i18n/deviceSetupContent";

type ConnectCode = { code: string; expiresAt: string };

export default function DesktopConnectClient() {
  const { locale } = useLanguage();
  const copy = getDeviceSetupContent(locale).desktopConnect;
  const [connection, setConnection] = useState<ConnectCode | null>(null);
  const [errorKey, setErrorKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const windowsPending = PUBLIC_PRODUCT_LINKS.interviewCopilotWindows === PUBLIC_STORE_DEFAULTS.windows;

  async function createCode() {
    setLoading(true);
    setErrorKey("");
    setCopied(false);
    try {
      const response = await fetch("/api/interview/v2/desktop/connect/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setErrorKey(response.status === 401 ? "signInError" : response.status === 403 ? "accessError" : "error");
        return;
      }
      setConnection(payload);
    } catch {
      setErrorKey("error");
    } finally {
      setLoading(false);
    }
  }

  async function copyCode() {
    if (!connection) return;
    await navigator.clipboard.writeText(connection.code);
    setCopied(true);
  }

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#ddd6fe,_transparent_36%),radial-gradient(circle_at_bottom_right,_#dbeafe,_transparent_34%),#f8fafc] px-4 py-8 text-slate-950 sm:px-6 sm:py-14">
      <section className="mx-auto w-full max-w-5xl overflow-hidden rounded-3xl border border-indigo-100 bg-white shadow-[0_24px_80px_rgba(49,46,129,0.14)]">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr]">
          <aside className="bg-gradient-to-br from-slate-950 via-indigo-950 to-violet-900 p-6 text-white sm:p-9">
            <a href="https://www.jobolar.ai/" aria-label="JOBolar.Ai home" className="inline-block rounded-xl transition hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/70">
              <Image src="/image/JobolarAiLogoV2.png" alt="JOBolar.Ai" width={170} height={68} className="h-auto w-40 rounded-xl bg-white p-2" priority />
            </a>
            <p className="mt-7 text-xs font-black uppercase tracking-[0.16em] text-indigo-200">{copy.setupTitle}</p>
            <p className="mt-3 text-sm leading-6 text-indigo-100">{copy.setupBody}</p>
            <div className="mt-5 grid gap-3">
              <a href={PUBLIC_PRODUCT_LINKS.interviewCopilotWindows} target="_blank" rel="noreferrer" className="rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-white/15">{windowsPending ? copy.windowsPending : copy.windows}</a>
              <MacDownloadModal label={copy.mac} triggerClassName="rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">{copy.mac}</MacDownloadModal>
            </div>
          </aside>

          <div className="p-6 sm:p-9 lg:p-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-indigo-600">{copy.eyebrow}</p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950">{copy.title}</h1>
            <p className="mt-4 text-sm leading-6 text-slate-600">{copy.intro}</p>
            <p className="mt-4 rounded-xl border border-violet-200 bg-violet-50 p-4 text-sm font-semibold text-violet-900">{copy.access}</p>
            <button type="button" disabled={loading} onClick={createCode} className="mt-6 rounded-xl bg-gradient-to-r from-indigo-700 to-violet-700 px-5 py-3 font-extrabold text-white shadow-lg shadow-indigo-200 disabled:opacity-50">
              {loading ? copy.creating : copy.create}
            </button>

            {connection ? (
              <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-5" role="status">
                <p className="text-sm font-medium text-slate-700">{copy.code}</p>
                <code className="mt-2 block break-all rounded bg-white p-3 text-sm text-slate-950">{connection.code}</code>
                <button type="button" className="mt-3 text-sm font-semibold text-blue-700" onClick={copyCode}>{copied ? copy.copied : copy.copy}</button>
                <p className="mt-3 text-xs text-slate-600">{formatDeviceSetupCopy(copy.expires, { time: new Date(connection.expiresAt).toLocaleTimeString(locale) })}</p>
              </div>
            ) : null}
            {errorKey ? <p className="mt-5 rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">{copy[errorKey] || copy.error}</p> : null}
            <p className="mt-6 border-t border-slate-100 pt-5 text-xs leading-5 text-slate-500">{copy.privacy}</p>
          </div>
        </div>
      </section>
    </main>
  );
}
